// Client-local "recently opened simulations" overlay (P3-FE-03, contract
// docs/design/P3-FE-03-my-sims-contract.md §4). Deliberately NOT a Pinia
// store (must be readable synchronously from a TanStack Query `select`) and
// NOT under `data/` (this is device-local UI state, not a server resource).
//
// The server has no recency concept in v1 — Phase 6 owns real progress
// tracking (`POST /v1/me/progress`). This module is purely a client overlay
// used by "My sims" (`data/my-sims.ts`) to re-order cards and pick the hero.
// The Player (P3-FE-04) is its only writer, via `touchRecentSim`; that
// ticket is independently shippable from this one — until it lands, My Sims
// simply shows an empty recency map (server order only).

const STORAGE_VERSION = 'v1';
/** Cap per contract §4 — truncated on every write. */
const MAX_ENTRIES = 20;

export interface RecentSimEntry {
  resourceId: string;
  /** ISO-8601 timestamp. */
  lastViewedAt: string;
}

/** Namespaced by user id (contract §4) — shared/lab devices are the norm for
 * this product; an unnamespaced key would show student A's history to
 * student B. */
function storageKey(userId: string): string {
  return `eskay:recent-sims:${STORAGE_VERSION}:${userId}`;
}

function isValidEntry(value: unknown): value is RecentSimEntry {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Record<string, unknown>).resourceId === 'string' &&
    typeof (value as Record<string, unknown>).lastViewedAt === 'string'
  );
}

/** Read the recency list for a user — newest first. Tolerant of an absent
 * key, unparseable JSON, or a shape-invalid value: always returns `[]` and
 * removes the bad key, so a corrupt localStorage value never throws into a
 * render. */
export function getRecentSims(userId: string): RecentSimEntry[] {
  const key = storageKey(userId);
  let raw: string | null;
  try {
    raw = localStorage.getItem(key);
  } catch {
    // Storage unavailable (private mode, disabled, etc.) — degrade to none.
    return [];
  }
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.every(isValidEntry)) {
      removeKey(key);
      return [];
    }
    return parsed;
  } catch {
    removeKey(key);
    return [];
  }
}

function removeKey(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // best-effort cleanup only
  }
}

/** Record (or refresh) a resource as most-recently-opened. Re-opening an
 * existing entry moves it to the front and refreshes its timestamp — never a
 * duplicate row. Truncates to `MAX_ENTRIES`. Wrapped in try/catch — a
 * `QuotaExceededError` degrades to "no recency ordering", never a broken
 * page. Written by the Player (P3-FE-04) whenever a `SIMULATION` resource
 * successfully loads. */
export function touchRecentSim(userId: string, resourceId: string, now: Date = new Date()): void {
  const existing = getRecentSims(userId).filter((entry) => entry.resourceId !== resourceId);
  const next: RecentSimEntry[] = [
    { resourceId, lastViewedAt: now.toISOString() },
    ...existing,
  ].slice(0, MAX_ENTRIES);
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(next));
  } catch {
    // QuotaExceededError or storage unavailable — degrade silently.
  }
}

/** Clear a user's recency history — called from `authStore.resetSession()`
 * (the shared teardown for user-initiated logout AND any forced-logout
 * command, P2-25 §5.4: `FORCE_LOGOUT` / `SESSION_SUPERSEDED`). Leaving one
 * student's sim history on a shared lab machine after sign-out is the
 * failure mode this exists to avoid. */
export function clearRecentSims(userId: string): void {
  removeKey(storageKey(userId));
}
